import React from 'react';
import { InputLabel, Select as MUISelect, FormControl, Box, FormHelperText, Stack } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Input from '../input';
import Button from '../button';
import { useTranslation } from 'react-i18next';
import InfiniteScrollList from '../infinite-scroll';

interface MultiselectProps<T> {
    selectedItems: T[];
    items: T[];
    hasMore?: boolean;
    fetchMoreData?: () => void;
    handleSearchChange?: (value: string) => void;
    handleItemChange: (event: React.ChangeEvent<HTMLInputElement>, item: T) => void;
    search?: string;
    renderLabel: (item: T) => React.ReactNode;
    itemIdentifier: (item: T) => string;
    label: string;
    searchPlaceholder?: string;
    clearAll?: () => void;
    selectAll?: () => void;
    isLoading?: boolean;
    id: string;
    error?: boolean;
    helperText?: string;
    openToTop?: boolean;
    showAll?: () => void;
    semiColumnDivider?: boolean;
    disabled?: boolean;
}

const Multiselect = <T,>({
    selectedItems,
    items,
    hasMore = false,
    fetchMoreData,
    handleSearchChange,
    handleItemChange,
    search = '',
    searchPlaceholder = '',
    renderLabel,
    itemIdentifier,
    label,
    clearAll,
    selectAll,
    id,
    error,
    helperText,
    openToTop = false,
    semiColumnDivider,
    disabled = false,
}: MultiselectProps<T>) => {
    const { t } = useTranslation();

    const onChange = (name: string, value: string) => {
        if (handleSearchChange) {
            handleSearchChange(value);
        }
    };

    return (
        <FormControl
            fullWidth
            variant='outlined'
            error={error}
            sx={{
                '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                        borderColor: 'common.grey2',
                        borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                        borderColor: 'common.black',
                    },
                    '&.Mui-focused fieldset': {
                        borderWidth: '1px',
                        borderColor: 'common.primary',
                    },
                    '&.Mui-error fieldset': {
                        borderColor: 'common.system.error.main',
                    },
                },
                '& .MuiFormHelperText-root.Mui-error': {
                    color: 'common.system.error.main',
                },
            }}
        >
            <InputLabel
                htmlFor='demo-multiple-checkbox-label'
                style={{
                    color: 'common.grey1',
                    fontSize: '14px',
                    background: 'white',
                    padding: '2px',
                    marginTop: '-2px',
                }}
                sx={{
                    '&.Mui-focused': {
                        color: error ? 'common.system.error.main' : 'common.grey1',
                    },
                    '&.MuiInputLabel-shrink': {
                        top: '3px !important',
                    },
                }}
            >
                {label}
            </InputLabel>
            <MUISelect
                data-testid='multiselect'
                multiple
                value={selectedItems.map(itemIdentifier)}
                renderValue={(selected) => (selected.length > 0 ? selected.join(semiColumnDivider ? '; ' : ', ') : '')}
                fullWidth
                IconComponent={KeyboardArrowDownIcon}
                disabled={disabled}
                MenuProps={{
                    PaperProps: {
                        style: {
                            overflow: 'none',
                        },
                    },
                    anchorOrigin: {
                        vertical: openToTop ? 'top' : 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: openToTop ? 'bottom' : 'top',
                        horizontal: 'left',
                    },
                }}
                sx={{
                    fontSize: '14px',
                    '&.MuiOutlinedInput-root': {
                        height: '48px',
                        '& fieldset': {
                            borderColor: 'common.grey2',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: 'common.grey1',
                        },
                        '&.Mui-focused fieldset': {
                            borderWidth: '1px',
                            borderColor: 'common.primary',
                        },
                    },
                    '&.MuiSelect-select': {
                        fontSize: '14px',
                        padding: '10px 26px 10px 12px',
                    },
                    '& .MuiSelect-icon': {
                        color: error ? 'common.system.error.main' : 'common.grey1',
                        transform: 'rotate(0deg)',
                        '&.MuiSelect-iconOpen': {
                            transform: 'rotate(180deg)',
                        },
                    },
                }}
            >
                <Box sx={{ padding: '20px 20px 0 20px' }}>
                    {handleSearchChange && (
                        <Input name='city-search' placeholder={searchPlaceholder} value={search} onChange={onChange} />
                    )}

                    {fetchMoreData && (
                        <InfiniteScrollList
                            items={items}
                            hasMore={hasMore}
                            fetchMoreData={fetchMoreData}
                            itemIdentifier={itemIdentifier}
                            renderLabel={renderLabel}
                            selectedItems={selectedItems}
                            handleItemChange={handleItemChange}
                            scrollableTarget={id}
                        />
                    )}
                    {(clearAll || selectAll) && (
                        <Stack direction='row' spacing={3}>
                            {clearAll && (
                                <Box sx={{ padding: '16px 0' }}>
                                    <Button
                                        label={t('clear-all')}
                                        size='large'
                                        variant='text'
                                        onClick={clearAll}
                                        sx={{ padding: 0, fontSize: '12px' }}
                                    />
                                </Box>
                            )}
                            {selectAll && (
                                <Box sx={{ padding: '16px 0' }}>
                                    <Button
                                        label={t('select-all')}
                                        size='large'
                                        variant='text'
                                        onClick={selectAll}
                                        sx={{ padding: 0, fontSize: '12px' }}
                                    />
                                </Box>
                            )}
                        </Stack>
                    )}
                </Box>
            </MUISelect>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};

export default Multiselect;
